import { z } from 'zod'
import { accessTokenNameSchema, accessTokenSchema } from './access-tokens'
import { boardColumnSchema, boardMoveResultSchema, boardViewSchema } from './board'
import {
  connectionSchema,
  connectorIdSchema,
  connectorInfoSchema,
  githubChoiceSchema,
  githubOptionsPatchSchema,
} from './connections'
import { calendarDate } from './dates'
import { aiModeSchema } from './env'
import { ingestOutcomeSchema, ingestSignalSchema } from './ingest'
import {
  itemHistoryEntrySchema,
  itemKindSchema,
  itemOriginSchema,
  itemPrioritySchema,
  itemSchema,
  itemSizeSchema,
  itemSourceSchema,
  itemTypeSchema,
  itemViewSchema,
  NOTES_MAX,
  PERSON_NAME_MAX,
  personSchema,
  SUGGESTION_REASON_MAX,
  snoozePresetSchema,
  suggestionConfidenceSchema,
  TITLE_MAX,
  tagsSchema,
  triageChoiceSchema,
} from './items'
import { outboxEntrySchema, REPLY_BODY_MAX } from './outbox'
import { estimateMinutesSchema, headsUpEpisodeSchema, headsUpSchema } from './plan'
import { userSettingsPatchSchema, userSettingsSchema } from './settings'
import { speechSessionSchema } from './speech'
import {
  awayStatusSchema,
  TIME_OFF_SUGGESTION_KEY_MAX,
  timeOffInputSchema,
  timeOffSchema,
  timeOffSuggestionSchema,
} from './timeoff'

/**
 * The tool registry contract. Every capability in hugo is a tool: the UI, the command palette,
 * voice, the agent and MCP clients all call these, never the database directly. Handlers live in
 * apps/api; this file defines names, input/output schemas and how each tool may be used.
 */
export type ToolEffect = 'read' | 'internal' | 'external'

export type ToolSpec<I extends z.ZodType = z.ZodType, O extends z.ZodType = z.ZodType> = {
  name: string
  title: string
  description: string
  /** read: no changes. internal: changes inside hugo. external: writes to another system. */
  effect: ToolEffect
  /** Whether the change can be undone with `action.undo`. */
  undoable: boolean
  /** Callable over HTTP and MCP. System tools (e.g. waking snoozed items) are not. */
  exposed: boolean
  /**
   * False: exposed to the user in hugo only, never to MCP clients (approving what hugo sends,
   * managing the tokens MCP clients sign in with). Default: offered over MCP when exposed.
   */
  mcp?: boolean
  input: I
  output: O
}

function tool<I extends z.ZodType, O extends z.ZodType>(spec: ToolSpec<I, O>): ToolSpec<I, O> {
  return spec
}

const iso = z.iso.datetime()
const id = z.uuid()
const title = z.string().trim().min(1, 'title is required').max(TITLE_MAX)
const personName = z.string().trim().min(1).max(PERSON_NAME_MAX)
const itemResult = z.object({ item: itemSchema })
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD')
/** Token counts are stored as 32-bit integers. */
const tokens = z.number().int().min(0).max(2_000_000_000)

/** The most an instruction for an AI reply draft may say ("say I'll send it Friday"). */
export const AI_INSTRUCTION_MAX = 500

/** What hugo's AI is set up to do, and what it cost today (UTC day) against the budget. */
export const aiStatusSchema = z.object({
  mode: aiModeSchema,
  /** mode !== 'off' */
  enabled: z.boolean(),
  /** The user's `aiAutoFile` setting. */
  autoFile: z.boolean(),
  today: z.object({ costUsd: z.number().min(0), budgetUsd: z.number().min(0) }),
  models: z.object({ triage: z.string(), draft: z.string() }),
})
export type AiStatus = z.output<typeof aiStatusSchema>

export const captureParseSchema = z.object({
  title: z.string(),
  kind: z.enum(['task', 'owe', 'waiting']),
  counterpartName: z.string().nullable(),
  counterpartKnown: z.boolean(),
  due: z.object({ at: iso, allDay: z.boolean(), text: z.string() }).nullable(),
  /** The #tags in the text, normalized (decision 97). */
  tags: z.array(z.string()).default([]),
  /** `!high` or `!low` in the text; null: not said (the item is `normal`). */
  priority: itemPrioritySchema.nullable().default(null),
})
export type CaptureParse = z.output<typeof captureParseSchema>

export const insightsSummarySchema = z.object({
  period: z.object({ start: iso, end: iso, label: z.string() }),
  cameIn: z.number().int(),
  done: z.number().int(),
  open: z.number().int(),
  previous: z.object({ cameIn: z.number().int(), done: z.number().int() }),
})
export type InsightsSummary = z.output<typeof insightsSummarySchema>

export const itemCountsSchema = z.object({
  inbox: z.number().int(),
  tasks: z.number().int(),
  owe: z.number().int(),
  waiting: z.number().int(),
  snoozed: z.number().int(),
  overdue: z.number().int(),
  /** Outbox entries waiting for approval. */
  outbox: z.number().int().default(0),
  /**
   * Active heads-ups: people who should hear from the user about a late promise or time off
   * (headsups.list, without the reminders).
   */
  headsUps: z.number().int().default(0),
  /** Active reminders: people the user waits on whose follow-up date has come (headsups.list). */
  reminders: z.number().int().default(0),
})
export type ItemCounts = z.output<typeof itemCountsSchema>

/** A meeting (or out-of-office time) on the user's calendar today. */
export const todayMeetingSchema = z.object({
  id: z.uuid(),
  kind: z.enum(['meeting', 'out_of_office']),
  title: z.string().nullable(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  url: z.string().nullable(),
})
export type TodayMeeting = z.output<typeof todayMeetingSchema>

/** Freed meeting time today and how hugo would fill it (see `today.brief`). */
export const freedTimeSchema = z.object({
  /** What `today.dismiss_freed` takes. */
  key: z.string().max(600),
  /** The meeting that no longer takes this time (from its calendar). */
  title: z.string().nullable(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  /** Free working minutes in it. */
  minutes: z.number().int().min(0),
  /** What to do in it, with the minutes each takes there. */
  items: z.array(z.object({ item: itemSchema, minutes: z.number().int().min(0) })),
})
export type FreedTime = z.output<typeof freedTimeSchema>

/** `today.brief`: the user's day from now, all from hugo's planner (never the AI). */
export const todayBriefSchema = z.object({
  /** The user's local date. */
  date: calendarDate,
  /** Today has working hours and is neither a public holiday nor time off. */
  workday: z.boolean(),
  /** The time off today falls in, if any. */
  timeOff: timeOffSchema.pick({ id: true, startsOn: true, endsOn: true, label: true }).nullable(),
  time: z.object({
    /** Working minutes left today. */
    workingMinutes: z.number().int().min(0),
    /** Minutes of meetings in them. */
    meetingMinutes: z.number().int().min(0),
    /** Minutes out of office (calendars) in them, beyond the meetings. */
    outOfOfficeMinutes: z.number().int().min(0).default(0),
    /** Minutes hugo plans for dated tasks and promises today (after meetings). */
    capacityMinutes: z.number().int().min(0),
    /** Of those, the minutes hugo's plan fills. */
    plannedMinutes: z.number().int().min(0),
  }),
  /** Today's meetings and out-of-office time from connected calendars, by start. */
  meetings: z.array(todayMeetingSchema),
  /** What hugo's plan does today, in plan order, with the minutes for each. */
  planned: z.array(z.object({ item: itemSchema, minutes: z.number().int().min(0) })),
  /** Open tasks and promises due today or earlier that get no time today (not personal ones). */
  due: z.array(itemSchema),
  /**
   * Personal tasks and promises for today (decision 97): due today or earlier, or put on today
   * or now on the board. By due date; they take none of the working hours.
   */
  personal: z.array(itemSchema).default([]),
  /** People waited on whose follow-up has come, when there is no reminder to send. */
  followUps: z.array(itemSchema),
  /** Who should hear from the user (as headsups.list). */
  headsUps: z.array(headsUpSchema),
  /** Items waiting in the inbox. */
  inbox: z.number().int().min(0),
  /** A calendar is connected and syncing: meetings count. */
  calendarConnected: z.boolean(),
  /** Out-of-office time in the calendar, now or within the time-off notice days (a rule), not time off yet. */
  timeOffSuggestions: z.array(timeOffSuggestionSchema).default([]),
  /**
   * Meeting time that became free today and is still ahead (decision 105): the earliest such
   * stretch, its free working minutes, and what hugo's planner suggests doing in it (late work
   * first, then priority, due date and fit). Null when there is none, or the user dismissed it.
   */
  freedTime: freedTimeSchema.nullable().default(null),
})
export type TodayBrief = z.output<typeof todayBriefSchema>

/** Where an item came from in another system, and whether hugo can answer there. */
export const itemSourceLinkSchema = z.object({
  connector: connectorIdSchema,
  url: z.string().nullable(),
  canReply: z.boolean(),
})
export type ItemSourceLink = z.output<typeof itemSourceLinkSchema>

const connectorInput = z.object({ connector: connectorIdSchema })
/**
 * `grant`: set only by hugo's OAuth callback (`/api/connect/<connector>/callback`), the id of the
 * access the provider just granted. Never a token.
 */
const connectInput = connectorInput.extend({
  grant: z
    .string()
    .regex(/^[\w-]{16,64}$/)
    .optional(),
})
const connectionResult = z.object({ connection: connectionSchema })
const outboxResult = z.object({ entry: outboxEntrySchema })
const periodResult = z.object({ period: timeOffSchema })

export const toolSpecs = {
  'items.list': tool({
    name: 'items.list',
    title: 'List items',
    description:
      'Items in one view: inbox; open (everything the user is responsible for: tasks, promises and waits, by due date); its parts tasks, owe (I owe: someone waits on the user) and waiting (waiting on: the user waits on someone); snoozed; done.',
    effect: 'read',
    undoable: false,
    exposed: true,
    input: z.object({ view: itemViewSchema, limit: z.number().int().min(1).max(500).default(200) }),
    output: z.object({ items: z.array(itemSchema) }),
  }),
  'items.counts': tool({
    name: 'items.counts',
    title: 'Count items',
    description:
      'Number of items per view, plus overdue open items, drafts waiting for approval and heads-ups.',
    effect: 'read',
    undoable: false,
    exposed: true,
    input: z.object({}),
    output: itemCountsSchema,
  }),
  'item.get': tool({
    name: 'item.get',
    title: 'Get an item',
    description: 'One item with its history and where it came from.',
    effect: 'read',
    undoable: false,
    exposed: true,
    input: z.object({ id }),
    output: z.object({
      item: itemSchema,
      history: z.array(itemHistoryEntrySchema),
      sources: z.array(itemSourceLinkSchema).default([]),
    }),
  }),
  'capture.parse': tool({
    name: 'capture.parse',
    title: 'Preview a capture',
    description:
      'Parse free text (English or German) into title, kind, person and due date without saving.',
    effect: 'read',
    undoable: false,
    exposed: true,
    input: z.object({ text: z.string().max(TITLE_MAX * 2) }),
    output: captureParseSchema,
  }),
  'item.capture': tool({
    name: 'item.capture',
    title: 'Capture',
    description:
      'Create an item from free text, e.g. "Send Anna the contract by Fri" (I owe Anna, due Friday). Optionally with the time it needs (estimateMinutes, 5–2400) and a place on the board (`place.column`: backlog, next_week, this_week, tomorrow, focus, today, now or waiting; `place.position` among the cards you put there, default last): in a work column a waiting-on text becomes a task, in waiting it is a waiting-on item. One action: one undo removes it.',
    effect: 'internal',
    undoable: true,
    exposed: true,
    input: z.object({
      text: z
        .string()
        .trim()
        .min(1)
        .max(TITLE_MAX * 2),
      estimateMinutes: estimateMinutesSchema.optional(),
      place: z
        .object({
          column: boardColumnSchema.exclude(['done']),
          position: z.number().int().min(0).max(10_000).optional(),
        })
        .optional(),
    }),
    output: z.object({ item: itemSchema, parsed: captureParseSchema }),
  }),
  'item.create': tool({
    name: 'item.create',
    title: 'Create an item',
    description: 'Create an item from structured fields.',
    effect: 'internal',
    undoable: true,
    exposed: true,
    input: z.object({
      title,
      kind: itemKindSchema.nullable().default(null),
      status: z.enum(['inbox', 'open']).default('open'),
      counterpartName: personName.nullable().default(null),
      dueAt: iso.nullable().default(null),
      dueAllDay: z.boolean().default(false),
      type: itemTypeSchema.nullable().default(null),
      origin: itemOriginSchema.default('own'),
      source: itemSourceSchema.default('hugo'),
      requestedAt: iso.nullable().default(null),
      notes: z.string().max(NOTES_MAX).nullable().default(null),
      tags: tagsSchema.default([]),
      /** Set by the user; left out, their priority rules decide. */
      priority: itemPrioritySchema.optional(),
    }),
    output: itemResult,
  }),
  'item.triage': tool({
    name: 'item.triage',
    title: 'Triage',
    description:
      'File an inbox item as task, I owe, waiting on or FYI, or ignore it. `due` sets the date asked for, `type` the kind of work and `priority` high or low (e.g. as hugo suggested) in the same change.',
    effect: 'internal',
    undoable: true,
    exposed: true,
    input: z.object({
      id,
      choice: triageChoiceSchema,
      /** For task, I owe and waiting on. Null or left out keeps the due date as it is. */
      due: z.object({ date: calendarDate }).nullable().optional(),
      /** For task, I owe and waiting on, when the item has no type yet (a set type is kept). */
      type: itemTypeSchema.nullable().optional(),
      /** High or low as the user's own (e.g. as hugo suggested); left out: their rules decide. */
      priority: z.enum(['high', 'low']).optional(),
    }),
    output: itemResult,
  }),
  'item.suggest': tool({
    name: 'item.suggest',
    title: 'Store a suggestion',
    description:
      "Store hugo's AI suggestion for an inbox item (replacing an open one) and record what the AI call cost. Changes nothing when the item left the inbox (system).",
    effect: 'internal',
    undoable: false,
    exposed: false,
    input: z.object({
      id,
      suggestion: z.object({
        choice: triageChoiceSchema,
        type: itemTypeSchema.nullable(),
        dueDate: calendarDate.nullable(),
        confidence: suggestionConfidenceSchema,
        priority: itemPrioritySchema.default('normal'),
        reason: z.string().trim().max(SUGGESTION_REASON_MAX),
      }),
      usage: z.object({
        model: z.string().min(1).max(200),
        mode: aiModeSchema,
        inputTokens: tokens,
        outputTokens: tokens,
        costMicroUsd: z.number().int().min(0),
      }),
    }),
    output: itemResult,
  }),
  'item.dismiss_suggestion': tool({
    name: 'item.dismiss_suggestion',
    title: 'Dismiss suggestion',
    description: "Dismiss hugo's suggestion for an item; the item stays where it is.",
    effect: 'internal',
    undoable: false,
    exposed: true,
    input: z.object({ id }),
    output: itemResult,
  }),
  'item.draft_reply': tool({
    name: 'item.draft_reply',
    title: 'Draft a reply with AI',
    description:
      "Ask hugo's AI for a reply to the latest message of the item's thread, optionally following an instruction (\"say I'll send it Friday\"). Returns text only: nothing is saved or sent; put it in the Outbox with item.reply.",
    effect: 'read',
    undoable: false,
    exposed: true,
    input: z.object({
      id,
      instruction: z.string().trim().max(AI_INSTRUCTION_MAX).nullable().default(null),
    }),
    output: z.object({ body: z.string(), mode: aiModeSchema }),
  }),
  'ai.status': tool({
    name: 'ai.status',
    title: 'AI status',
    description:
      "Whether hugo's AI is on, whether it files clear asks by itself, and today's AI cost against the daily budget.",
    effect: 'read',
    undoable: false,
    exposed: true,
    input: z.object({}),
    output: aiStatusSchema,
  }),
  'item.set_kind': tool({
    name: 'item.set_kind',
    title: 'Set who is waiting',
    description:
      'Say who is waiting: nobody (task), someone waits on the user (owe: I owe; hugo plans it and drafts a note if it slips), or the user waits on someone (waiting: waiting on; hugo drafts reminders). An inbox item is filed with it.',
    effect: 'internal',
    undoable: true,
    exposed: true,
    input: z.object({ id, kind: z.enum(['task', 'owe', 'waiting']) }),
    output: itemResult,
  }),
  'item.update': tool({
    name: 'item.update',
    title: 'Edit an item',
    description:
      'Change title, notes, type, size, tags, priority (high, normal or low: hugo does high-priority work first where nothing dated goes late; null: your priority rules decide again) or the time needed (estimateMinutes, 5–2400; null: hugo assumes a default by type).',
    effect: 'internal',
    undoable: true,
    exposed: true,
    input: z.object({
      id,
      title: title.optional(),
      notes: z.string().max(NOTES_MAX).nullable().optional(),
      type: itemTypeSchema.nullable().optional(),
      size: itemSizeSchema.nullable().optional(),
      tags: tagsSchema.optional(),
      priority: itemPrioritySchema.nullable().optional(),
      estimateMinutes: estimateMinutesSchema.nullable().optional(),
    }),
    output: itemResult,
  }),
  'item.set_due': tool({
    name: 'item.set_due',
    title: 'Set due date',
    description:
      'Set or clear the due date. For I owe items this is the date promised to the person.',
    effect: 'internal',
    undoable: true,
    exposed: true,
    input: z.object({
      id,
      due: z.union([z.object({ date: dateOnly }), z.object({ at: iso }), z.null()]),
    }),
    output: itemResult,
  }),
  'item.set_counterpart': tool({
    name: 'item.set_counterpart',
    title: 'Set person',
    description: 'Set who is on the other side (who I owe, or who I wait on). Null clears it.',
    effect: 'internal',
    undoable: true,
    exposed: true,
    input: z.object({ id, name: personName.nullable() }),
    output: itemResult,
  }),
  'item.snooze': tool({
    name: 'item.snooze',
    title: 'Snooze',
    description: 'Hide an item until a time; it comes back to where it was.',
    effect: 'internal',
    undoable: true,
    exposed: true,
    input: z
      .object({ id, preset: snoozePresetSchema.optional(), until: iso.optional() })
      .refine((v) => Boolean(v.preset) !== Boolean(v.until), 'give either preset or until'),
    output: itemResult,
  }),
  'item.complete': tool({
    name: 'item.complete',
    title: 'Mark done',
    description:
      'Mark an item done. For a GitHub issue or pull request, `github` says what happens there too: `testing` moves it to the Testing column of its GitHub Projects board, `close` closes the issue as completed (not pull requests), `hugo` (the default) changes nothing on GitHub. Undo puts GitHub back as well.',
    effect: 'internal',
    undoable: true,
    exposed: true,
    input: z.object({ id, github: githubChoiceSchema.optional() }),
    output: itemResult,
  }),
  'item.drop': tool({
    name: 'item.drop',
    title: 'Drop',
    description: 'Drop an item without doing it.',
    effect: 'internal',
    undoable: true,
    exposed: true,
    input: z.object({ id }),
    output: itemResult,
  }),
  'item.reopen': tool({
    name: 'item.reopen',
    title: 'Reopen',
    description: 'Reopen a done or dropped item.',
    effect: 'internal',
    undoable: true,
    exposed: true,
    input: z.object({ id }),
    output: itemResult,
  }),
  'item.wake': tool({
    name: 'item.wake',
    title: 'Wake',
    description: 'Bring a snoozed item back when its time has come (system).',
    effect: 'internal',
    undoable: false,
    exposed: false,
    input: z.object({ id }),
    output: itemResult,
  }),
  'action.undo': tool({
    name: 'action.undo',
    title: 'Undo',
    description: 'Undo an earlier change, if nothing changed the items since.',
    effect: 'internal',
    undoable: false,
    exposed: true,
    input: z.object({ actionId: id }),
    output: z.object({ undone: z.literal(true), tool: z.string() }),
  }),
  'people.search': tool({
    name: 'people.search',
    title: 'Find people',
    description: 'People hugo knows, matching a name prefix.',
    effect: 'read',
    undoable: false,
    exposed: true,
    input: z.object({
      q: z.string().max(PERSON_NAME_MAX).default(''),
      limit: z.number().int().min(1).max(50).default(10),
    }),
    output: z.object({ people: z.array(personSchema) }),
  }),
  'settings.get': tool({
    name: 'settings.get',
    title: 'Get settings',
    description:
      'Time zone, working hours, holiday calendar, week start, whether hugo files clear asks by itself, and the minutes per working day hugo plans for dated work.',
    effect: 'read',
    undoable: false,
    exposed: true,
    input: z.object({}),
    output: userSettingsSchema,
  }),
  'settings.update': tool({
    name: 'settings.update',
    title: 'Update settings',
    description:
      'Change time zone, working hours, holiday calendar, week start, whether hugo files clear asks by itself (aiAutoFile), or the minutes per working day hugo plans for dated work (planningMinutesPerDay, 30–720 in steps of 30). `rules` changes the numbers hugo decides with, only the ones named: estimateMinutes by type (reply, decision, admin, review, meeting, fix, deliverable, other, untyped; 5–2400), reminderIntervalWorkingDays (1–20), awaySoonDays (0–60), timeOffNoticeDays (1–60), undatedNoticeWorkingDays (1–20), focusBlockMinutes (15–240, steps of 15), priorityRules (the whole list, in order, at most 20; the first that matches decides the priority of items you have not set: `{ when: { kind: "tag", tag } | { kind: "person", name } | { kind: "type", type } | { kind: "due_within", days }, priority: "high" | "low" }`).',
    effect: 'internal',
    undoable: false,
    exposed: true,
    input: userSettingsPatchSchema,
    output: userSettingsSchema,
  }),
  'time_off.list': tool({
    name: 'time_off.list',
    title: 'Time off',
    description:
      'Days off (e.g. vacation): periods that ended in the last 90 days, current and future ones, by first day. Time off is not working time. Also suggestions: out-of-office time in your calendar that is not time off yet (add one with time_off.add, or dismiss it).',
    effect: 'read',
    undoable: false,
    exposed: true,
    input: z.object({}),
    output: z.object({
      periods: z.array(timeOffSchema),
      suggestions: z.array(timeOffSuggestionSchema).default([]),
      /** Away now or soon, from all the user's time off (see awayStatusSchema). */
      away: awayStatusSchema.default(null),
    }),
  }),
  'time_off.add': tool({
    name: 'time_off.add',
    title: 'Add time off',
    description:
      'Add whole days off, first and last day included (dates in your time zone, at most 366 days), with an optional label such as "Vacation". Periods may overlap.',
    effect: 'internal',
    undoable: false,
    exposed: true,
    input: timeOffInputSchema,
    output: periodResult,
  }),
  'time_off.remove': tool({
    name: 'time_off.remove',
    title: 'Remove time off',
    description: 'Remove a time-off period.',
    effect: 'internal',
    undoable: false,
    exposed: true,
    input: z.object({ id }),
    output: z.object({ removed: z.literal(true) }),
  }),
  'time_off.dismiss_suggestion': tool({
    name: 'time_off.dismiss_suggestion',
    title: 'Dismiss time-off suggestion',
    description:
      'Do not suggest this out-of-office time as time off again (by its key from time_off.list). New dates in the calendar are a new suggestion.',
    effect: 'internal',
    undoable: false,
    exposed: true,
    input: z.object({ key: z.string().min(1).max(TIME_OFF_SUGGESTION_KEY_MAX) }),
    output: z.object({ dismissed: z.literal(true) }),
  }),
  'insights.summary': tool({
    name: 'insights.summary',
    title: 'This week',
    description: 'Items that came in and got done this week, and how many are open now.',
    effect: 'read',
    undoable: false,
    exposed: true,
    input: z.object({ period: z.literal('this_week').default('this_week') }),
    output: insightsSummarySchema,
  }),
  'connectors.list': tool({
    name: 'connectors.list',
    title: 'List connectors',
    description:
      'Every system hugo can collect from, whether it is available yet, the connection if there is one, and whether sending is switched on.',
    effect: 'read',
    undoable: false,
    exposed: true,
    input: z.object({}),
    output: z.object({ connectors: z.array(connectorInfoSchema), writesEnabled: z.boolean() }),
  }),
  'connection.connect': tool({
    name: 'connection.connect',
    title: 'Connect',
    description:
      'Connect a system and start syncing. Google systems (Gmail, Calendar, Chat, Drive) use the signed-in Google account; others (GitHub) need the user to grant access in the browser first: without it, the answer says where (`needsAuthorize`, `url`).',
    effect: 'internal',
    undoable: false,
    exposed: true,
    input: connectInput,
    output: connectionResult,
  }),
  'connection.update_options': tool({
    name: 'connection.update_options',
    title: 'GitHub options',
    description:
      "Change GitHub's options: `testingStatus` (the Projects board column that means testing), `doneWhen` (`testing_or_closed`: Testing already makes an assigned item done in hugo; `closed`: only closing the issue does), `lastChoice` (what done in hugo does on GitHub by default: testing, close or hugo).",
    effect: 'internal',
    undoable: false,
    exposed: true,
    input: z.object({ connector: z.literal('github'), github: githubOptionsPatchSchema }),
    output: connectionResult,
  }),
  'connection.disconnect': tool({
    name: 'connection.disconnect',
    title: 'Disconnect',
    description: 'Stop syncing a system. Items already collected stay.',
    effect: 'internal',
    undoable: false,
    exposed: true,
    input: connectorInput,
    output: connectionResult,
  }),
  'connection.sync': tool({
    name: 'connection.sync',
    title: 'Sync now',
    description: 'Queue a sync of a connected system now.',
    effect: 'internal',
    undoable: false,
    exposed: true,
    input: connectorInput,
    output: z.object({ queued: z.literal(true) }),
  }),
  'item.ingest': tool({
    name: 'item.ingest',
    title: 'Ingest a signal',
    description:
      'Create, update or close the item for a signal from a connector, e.g. a new email in a thread or an answered invitation (system).',
    effect: 'internal',
    undoable: false,
    exposed: false,
    input: ingestSignalSchema,
    /** `item` is null when a settled ask had no item: ingest never creates one for it. */
    output: z.object({ item: itemSchema.nullable(), outcome: ingestOutcomeSchema }),
  }),
  'item.reply': tool({
    name: 'item.reply',
    title: 'Draft a reply',
    description:
      "Draft a reply in the item's thread to the last person who wrote. It waits in the outbox until approved; nothing is sent by this tool.",
    effect: 'internal',
    undoable: false,
    exposed: true,
    input: z.object({ id, body: z.string().trim().min(1, 'body is required').max(REPLY_BODY_MAX) }),
    output: outboxResult,
  }),
  'outbox.list': tool({
    name: 'outbox.list',
    title: 'Outbox',
    description:
      'Drafts waiting for approval (pending), or recently decided ones: sent, dry run, failed, discarded, or withdrawn by hugo because they were no longer needed (recent).',
    effect: 'read',
    undoable: false,
    exposed: true,
    input: z.object({ status: z.enum(['pending', 'recent']).default('pending') }),
    output: z.object({ entries: z.array(outboxEntrySchema), writesEnabled: z.boolean() }),
  }),
  'outbox.approve': tool({
    name: 'outbox.approve',
    title: 'Approve and send',
    description:
      'Send a pending draft to its recipients in its thread. With sending switched off it is recorded as a dry run.',
    effect: 'external',
    undoable: false,
    exposed: true,
    // Only the user, in hugo, lets something leave: never an MCP client (decision 99).
    mcp: false,
    input: z.object({ id }),
    output: outboxResult,
  }),
  'outbox.discard': tool({
    name: 'outbox.discard',
    title: 'Discard draft',
    description: 'Throw away a pending draft without sending it.',
    effect: 'internal',
    undoable: false,
    exposed: true,
    input: z.object({ id }),
    output: outboxResult,
  }),
  'headsups.list': tool({
    name: 'headsups.list',
    title: 'Heads-ups and reminders',
    description:
      'Who should hear from you: people you promised something that will be late (with the date the work can be done), people you wait on whose follow-up date has come and who have not written since it was set (a reminder), and people you wait on before your time off. Late promises first by due date, then reminders by follow-up date, then time off by ask-by date.',
    effect: 'read',
    undoable: false,
    exposed: true,
    input: z.object({}),
    output: z.object({ headsUps: z.array(headsUpSchema) }),
  }),
  'headsup.draft': tool({
    name: 'headsup.draft',
    title: 'Add heads-up to Outbox',
    description:
      "Put a heads-up or reminder in the Outbox as a reply in the item's thread (only for the reply channel). For a late promise, `newDate` (not before today) replaces the suggested date; approving the entry moves the due date there. Approving a reminder moves a follow-up date that has come on by your reminder interval (rules, default 3 working days). Nothing is sent by this tool.",
    effect: 'internal',
    undoable: false,
    exposed: true,
    input: z.object({ episode: headsUpEpisodeSchema, newDate: calendarDate.optional() }),
    output: outboxResult,
  }),
  'headsup.dismiss': tool({
    name: 'headsup.dismiss',
    title: 'Dismiss heads-up',
    description:
      'Mark a heads-up or reminder as handled ("I told them", "I reminded them") so hugo does not suggest it again. For a late promise, `setDue` moves the due date to the date you told them (not before today); for a reminder, it sets the next follow-up date (after today).',
    effect: 'internal',
    undoable: false,
    exposed: true,
    input: z.object({
      episode: headsUpEpisodeSchema,
      setDue: z.object({ date: calendarDate }).optional(),
    }),
    output: z.object({ dismissed: z.literal(true) }),
  }),
  'voice.session': tool({
    name: 'voice.session',
    title: 'Start dictating',
    description:
      'A short-lived session for the speech service: the device streams audio there and gets the transcript back; only the text comes to hugo. In your speech language (settings).',
    effect: 'read',
    undoable: false,
    // The apps call it over HTTP. An agent has text, not a microphone, but a token in its hands
    // only opens a 60-second speech session for the same user.
    exposed: true,
    input: z.object({}),
    output: z.object({ session: speechSessionSchema }),
  }),
  'today.brief': tool({
    name: 'today.brief',
    title: 'Today',
    description:
      "Your day from now: the time left for tasks and promises after meetings, hugo's plan for today (items and minutes), what else is due, who to tell or remind, follow-ups that have come, today's meetings from your calendar and how many items wait in the inbox. Every number comes from hugo's planner.",
    effect: 'read',
    undoable: false,
    exposed: true,
    input: z.object({}),
    output: todayBriefSchema,
  }),
  'tags.list': tool({
    name: 'tags.list',
    title: 'Tags',
    description:
      "The tags of the user's active items (inbox, open, snoozed) with how many carry each, and whether a tag is personal (its items stay out of the working hours hugo plans; change with settings.update personalTags). Personal tags no item carries are listed too.",
    effect: 'read',
    undoable: false,
    exposed: true,
    input: z.object({}),
    output: z.object({
      tags: z.array(
        z.object({ name: z.string(), open: z.number().int().min(0), personal: z.boolean() }),
      ),
    }),
  }),
  'today.dismiss_freed': tool({
    name: 'today.dismiss_freed',
    title: 'Dismiss freed time',
    description:
      "Stop suggesting how to fill this freed meeting time (by its key from today.brief's freedTime).",
    effect: 'internal',
    undoable: false,
    exposed: true,
    input: z.object({ key: z.string().min(1).max(600) }),
    output: z.object({ dismissed: z.literal(true) }),
  }),
  'board.view': tool({
    name: 'board.view',
    title: 'Board',
    description:
      "Your open work in columns: backlog (by priority), next week, this week, tomorrow (the next working day), today's focus work, the rest of today, now, waiting for others, and done this week. Cards you put in a column come first, in your order; hugo's own cards follow where its plan does them. Each column with days shows the time for tasks there and what hugo's plan fills; today also the focus time (free blocks of at least your focus block length, default an hour). Every number comes from hugo's planner.",
    effect: 'read',
    undoable: false,
    exposed: true,
    input: z.object({}),
    output: boardViewSchema,
  }),
  'item.move': tool({
    name: 'item.move',
    title: 'Move on the board',
    description:
      'Put a task, promise or waiting-on item in a board column; hugo replans everything at once. backlog: not planned yet (after everything else). next_week, this_week, tomorrow: planned then, no time before. focus: today, before anything else of today. today: today. now: being done, first of all. waiting: waiting for someone (a waiting-on item; a task or promise becomes one). done: mark it done. A done item moved to a work column is reopened; a snoozed one moved to today or now wakes up. `position`: its place among the cards you put in that column (0: first; default: last). Returns the item and what changed for other cards (moved by hugo, late now or on time again).',
    effect: 'internal',
    undoable: true,
    exposed: true,
    input: z.object({
      id,
      column: boardColumnSchema,
      position: z.number().int().min(0).max(10_000).optional(),
    }),
    output: boardMoveResultSchema,
  }),
  'access_tokens.list': tool({
    name: 'access_tokens.list',
    title: 'Access tokens',
    description: 'The tokens that let tools outside hugo (Claude Code over MCP) act as you.',
    effect: 'read',
    undoable: false,
    exposed: true,
    mcp: false,
    input: z.object({}),
    output: z.object({ tokens: z.array(accessTokenSchema) }),
  }),
  'access_tokens.create': tool({
    name: 'access_tokens.create',
    title: 'Create an access token',
    description:
      'A new token for a tool outside hugo. The secret is in this answer only; hugo keeps its hash.',
    effect: 'internal',
    undoable: false,
    exposed: true,
    mcp: false,
    input: z.object({ name: accessTokenNameSchema }),
    output: z.object({ token: accessTokenSchema, secret: z.string() }),
  }),
  'access_tokens.revoke': tool({
    name: 'access_tokens.revoke',
    title: 'Revoke an access token',
    description: 'The token stops working at once.',
    effect: 'internal',
    undoable: false,
    exposed: true,
    mcp: false,
    input: z.object({ id }),
    output: z.object({ revoked: z.literal(true) }),
  }),
} as const

/**
 * A tool's input as JSON Schema, for clients that call tools by schema (GET /api/tools, MCP).
 * Every tool takes an object; a spec that did not would fail here, at startup.
 */
export function toolInputJsonSchema(
  spec: Pick<ToolSpec, 'name' | 'input'>,
): Record<string, unknown> & { type: 'object' } {
  const schema = z.toJSONSchema(spec.input, { io: 'input', unrepresentable: 'any' })
  if (schema.type !== 'object') throw new Error(`${spec.name}: the input must be an object`)
  return { ...schema, type: 'object' }
}

/** Whether MCP clients may see and call a tool (see ToolSpec.mcp). */
export function isMcpTool(spec: Pick<ToolSpec, 'exposed' | 'mcp'>): boolean {
  return spec.exposed && spec.mcp !== false
}

export type ToolSpecs = typeof toolSpecs
export type ToolName = keyof ToolSpecs
export type ToolInput<N extends ToolName> = z.input<ToolSpecs[N]['input']>
export type ToolOutput<N extends ToolName> = z.output<ToolSpecs[N]['output']>

export function isToolName(name: string): name is ToolName {
  return Object.hasOwn(toolSpecs, name)
}

/** The envelope POST /api/tools/:name returns. */
export const toolResponseSchema = z.object({
  result: z.unknown(),
  action: z.object({ id: z.uuid(), tool: z.string(), undoable: z.boolean() }).nullable(),
})
export type ToolResponse = z.output<typeof toolResponseSchema>
